import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import ApexCharts from 'react-apexcharts';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const BoekingenStatistieken = ({ username }) => {
  const [statistieken, setStatistieken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [periode, setPeriode] = useState('week'); // week, maand, jaar
  const [sortBy, setSortBy] = useState('omzet'); // 'omzet' of 'aantal'
  const [specificFilter, setSpecificFilter] = useState(''); // ID van specifiek arrangement/ervaring
  const [availableItems, setAvailableItems] = useState({ arrangementen: [], ervaringen: [] });
  const [datumRange, setDatumRange] = useState({
    startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD')
  });
  const [tempDatumRange, setTempDatumRange] = useState({
    startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD')
  });
  const pdfRef = useRef();

  useEffect(() => {
    loadStatistieken();
  }, [periode, datumRange, sortBy, specificFilter]);

  useEffect(() => {
    loadAvailableItems();
  }, []);

  useEffect(() => {
    setTempDatumRange(datumRange);
  }, [datumRange]);

  const generatePDF = async () => {
    if (!pdfRef.current) return;

    try {
      const element = pdfRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`boekingen-statistieken-${moment().format('YYYY-MM-DD')}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Er is een fout opgetreden bij het genereren van de PDF');
    }
  };

  const loadStatistieken = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/boekingen/statistieken', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('token')
        },
        body: JSON.stringify({
          periode,
          startDate: datumRange.startDate,
          endDate: datumRange.endDate,
          sortBy,
          specificFilter
        })
      });

      const data = await response.json();
      if (data.success) {
        setStatistieken(data.statistieken);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodeChange = (newPeriode) => {
    setPeriode(newPeriode);

    // Auto-adjust date range based on period
    let startDate, endDate = moment();
    switch (newPeriode) {
      case 'week':
        startDate = moment().subtract(7, 'days');
        break;
      case 'maand':
        startDate = moment().subtract(30, 'days');
        break;
      case 'kwartaal':
        startDate = moment().subtract(3, 'months');
        break;
      case 'jaar':
        startDate = moment().subtract(1, 'year');
        break;
      default:
        startDate = moment().subtract(30, 'days');
    }

    const newRange = {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD')
    };
    setDatumRange(newRange);
    setTempDatumRange(newRange);
  };

  const applyDateRange = () => {
    setDatumRange(tempDatumRange);
  };

  const loadAvailableItems = async () => {
    try {
      // Laad arrangementen
      const arrangementenResponse = await fetch('/api/admin/arrangementen', {
        method: 'GET',
        headers: {
          'Authorization': localStorage.getItem('token')
        }
      });
      const arrangementenData = await arrangementenResponse.json();

      // Laad ervaringen
      const ervaringenResponse = await fetch('/api/admin/ervaringen', {
        method: 'GET',
        headers: {
          'Authorization': localStorage.getItem('token')
        }
      });
      const ervaringenData = await ervaringenResponse.json();

      console.log('Arrangementen:', arrangementenData);
      console.log('Ervaringen:', ervaringenData);

      // Ensure we always have arrays
      const arrangementen = Array.isArray(arrangementenData.results)
        ? arrangementenData.results
        : Array.isArray(arrangementenData)
        ? arrangementenData
        : [];

      const ervaringen = Array.isArray(ervaringenData.results)
        ? ervaringenData.results
        : Array.isArray(ervaringenData)
        ? ervaringenData
        : [];

      setAvailableItems({
        arrangementen,
        ervaringen
      });
    } catch (error) {
      console.error('Error loading available items:', error);
      // Set empty arrays on error
      setAvailableItems({
        arrangementen: [],
        ervaringen: []
      });
    }
  };

  // ApexCharts configurations
  const getOmzetChartOptions = () => {
    if (!statistieken?.periodenData) return null;

    return {
      chart: {
        type: 'area',
        height: 350,
        fontFamily: 'inherit',
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false
          }
        }
      },
      stroke: {
        curve: 'smooth',
        width: 3
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.3,
          opacityTo: 0.05,
          stops: [0, 100]
        }
      },
      colors: ['#d96a29'],
      dataLabels: {
        enabled: false
      },
      xaxis: {
        categories: statistieken.periodenData.map(item => item.periode),
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      },
      yaxis: {
        labels: {
          formatter: function(value) {
            return '€' + value.toLocaleString('nl-NL');
          }
        }
      },
      tooltip: {
        y: {
          formatter: function(value) {
            return '€' + value.toLocaleString('nl-NL');
          }
        }
      },
      grid: {
        borderColor: '#f1f1f1',
        strokeDashArray: 4
      }
    };
  };

  const getOmzetChartSeries = () => {
    if (!statistieken?.periodenData) return [];

    return [{
      name: 'Omzet',
      data: statistieken.periodenData.map(item => item.omzet)
    }];
  };

  const getBoekingTypesOptions = () => {
    if (!statistieken?.typeData) return null;

    return {
      chart: {
        type: 'donut',
        height: 350,
        fontFamily: 'inherit'
      },
      labels: statistieken.typeData.map(item => item.type),
      colors: ['#d96a29', '#71dd37', '#03c3ec', '#ffab00'],
      plotOptions: {
        pie: {
          donut: {
            size: '60%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Totaal',
                formatter: function (w) {
                  return w.globals.seriesTotals.reduce((a, b) => {
                    return a + b;
                  }, 0);
                }
              }
            }
          }
        }
      },
      legend: {
        position: 'bottom'
      },
      tooltip: {
        y: {
          formatter: function(value) {
            return value + ' boekingen';
          }
        }
      }
    };
  };

  const getBoekingTypesSeries = () => {
    if (!statistieken?.typeData) return [];
    return statistieken.typeData.map(item => item.aantal);
  };

  const getAantalBoekingenChartOptions = () => {
    if (!statistieken?.periodenData) return null;

    return {
      chart: {
        type: 'bar',
        height: 350,
        fontFamily: 'inherit'
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          endingShape: 'rounded'
        },
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: statistieken.periodenData.map(item => item.periode),
      },
      yaxis: {
        title: {
          text: 'Aantal Boekingen'
        }
      },
      fill: {
        opacity: 1
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val + " boekingen"
          }
        }
      },
      colors: ['#71dd37']
    };
  };

  const getAantalBoekingenSeries = () => {
    if (!statistieken?.periodenData) return [];

    return [{
      name: 'Aantal Boekingen',
      data: statistieken.periodenData.map(item => item.aantalBoekingen)
    }];
  };

  // Booking Advance Time Charts
  const getVooruitBoekenChartOptions = () => {
    if (!statistieken?.vooruitBoekenData) return null;

    return {
      chart: {
        type: 'line',
        height: 350,
        fontFamily: 'inherit',
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false
          }
        }
      },
      stroke: {
        curve: 'smooth',
        width: 4
      },
      colors: ['#8b5cf6'],
      dataLabels: {
        enabled: true,
        formatter: function(value) {
          return Math.round(value) + ' dagen';
        },
        style: {
          fontSize: '11px',
          fontWeight: 'bold'
        }
      },
      xaxis: {
        categories: statistieken.vooruitBoekenData.map(item => item.periode),
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      },
      yaxis: {
        title: {
          text: 'Dagen van Tevoren'
        },
        labels: {
          formatter: function(value) {
            return Math.round(value) + ' dagen';
          }
        }
      },
      tooltip: {
        y: {
          formatter: function(value) {
            return Math.round(value) + ' dagen gemiddeld van tevoren geboekt';
          }
        }
      },
      grid: {
        borderColor: '#f1f1f1',
        strokeDashArray: 4
      },
      markers: {
        size: 6,
        colors: ['#8b5cf6'],
        strokeColors: '#fff',
        strokeWidth: 2,
        hover: {
          size: 8
        }
      }
    };
  };

  const getVooruitBoekenSeries = () => {
    if (!statistieken?.vooruitBoekenData) return [];

    return [{
      name: 'Gemiddelde Dagen van Tevoren',
      data: statistieken.vooruitBoekenData.map(item => Math.round(item.gemiddeldeDagen * 10) / 10)
    }];
  };

  const getBoekingTijdspanneOptions = () => {
    if (!statistieken?.tijdspanneData) return null;

    return {
      chart: {
        type: 'donut',
        height: 350,
        fontFamily: 'inherit'
      },
      labels: statistieken.tijdspanneData.map(item => item.tijdspanne),
      colors: ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6'],
      plotOptions: {
        pie: {
          donut: {
            size: '60%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Totaal',
                formatter: function (w) {
                  return w.globals.seriesTotals.reduce((a, b) => {
                    return a + b;
                  }, 0) + ' boekingen';
                }
              }
            }
          }
        }
      },
      legend: {
        position: 'bottom',
        fontSize: '12px'
      },
      tooltip: {
        y: {
          formatter: function(value) {
            return value + ' boekingen';
          }
        }
      }
    };
  };

  const getBoekingTijdspanneSeries = () => {
    if (!statistieken?.tijdspanneData) return [];
    return statistieken.tijdspanneData.map(item => item.aantal);
  };

  // Top Arrangementen Chart
  const getTopArrangementenOptions = () => {
    if (!statistieken?.topArrangementen || statistieken.topArrangementen.length === 0) return null;

    return {
      chart: {
        type: 'bar',
        height: 400,
        fontFamily: 'inherit'
      },
      plotOptions: {
        bar: {
          horizontal: true,
          dataLabels: {
            position: 'top',
          },
        }
      },
      dataLabels: {
        enabled: true,
        offsetX: -6,
        style: {
          fontSize: '12px',
          colors: ['#fff']
        },
        formatter: function (val) {
          return sortBy === 'omzet' ? '€' + val.toLocaleString('nl-NL') : val + ' boekingen';
        }
      },
      stroke: {
        show: true,
        width: 1,
        colors: ['#fff']
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: function (val) {
            return sortBy === 'omzet' ? '€' + val.toLocaleString('nl-NL') : val + ' boekingen';
          }
        }
      },
      xaxis: {
        categories: statistieken.topArrangementen.map(item => item.naam),
        labels: {
          formatter: function (val) {
            return sortBy === 'omzet' ? '€' + val.toLocaleString('nl-NL') : val;
          }
        }
      },
      colors: ['#d96a29']
    };
  };

  const getTopArrangementenSeries = () => {
    if (!statistieken?.topArrangementen) return [];

    return [{
      name: sortBy === 'omzet' ? 'Omzet' : 'Aantal Boekingen',
      data: statistieken.topArrangementen.map(item => sortBy === 'omzet' ? item.omzet : item.aantal)
    }];
  };

  // Top Ervaringen Chart
  const getTopErvaringenOptions = () => {
    if (!statistieken?.topErvaringen || statistieken.topErvaringen.length === 0) return null;

    return {
      chart: {
        type: 'bar',
        height: 400,
        fontFamily: 'inherit'
      },
      plotOptions: {
        bar: {
          horizontal: true,
          dataLabels: {
            position: 'top',
          },
        }
      },
      dataLabels: {
        enabled: true,
        offsetX: -6,
        style: {
          fontSize: '12px',
          colors: ['#fff']
        },
        formatter: function (val) {
          return sortBy === 'omzet' ? '€' + val.toLocaleString('nl-NL') : val + ' boekingen';
        }
      },
      stroke: {
        show: true,
        width: 1,
        colors: ['#fff']
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: function (val) {
            return sortBy === 'omzet' ? '€' + val.toLocaleString('nl-NL') : val + ' boekingen';
          }
        }
      },
      xaxis: {
        categories: statistieken.topErvaringen.map(item => item.naam),
        labels: {
          formatter: function (val) {
            return sortBy === 'omzet' ? '€' + val.toLocaleString('nl-NL') : val;
          }
        }
      },
      colors: ['#71dd37']
    };
  };

  const getTopErvaringenSeries = () => {
    if (!statistieken?.topErvaringen) return [];

    return [{
      name: sortBy === 'omzet' ? 'Omzet' : 'Aantal Boekingen',
      data: statistieken.topErvaringen.map(item => sortBy === 'omzet' ? item.omzet : item.aantal)
    }];
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Laden...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="box box-default">
      <div className="box-header with-border">
        <h3 className="box-title">
          <i className="fas fa-chart-bar me-2 text-primary"></i>
          Boekingen Statistieken
        </h3>
        <div className="box-tools pull-right">
          <button
            className="btn btn-danger btn-sm"
            onClick={generatePDF}
            disabled={loading || !statistieken}
            title="Download rapport als PDF"
          >
            <i className="fas fa-file-pdf me-2"></i>
            Download Rapport
          </button>
        </div>
      </div>

      <div className="box-body">
        <div className="container-fluid">
          {/* Filters */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="card-title mb-0">
                <i className="fas fa-filter me-2"></i>
                Filters
              </h6>
            </div>
            <div className="card-body">
              {/* Eerste rij: Periode, Specifiek Item en Sorteren */}
              <div className="row mb-3">
                <div className="col-md-4">
                  <label className="form-label fw-bold text-muted">Periode</label>
                  <div className="btn-group w-100" role="group">
                    {['week', 'maand', 'kwartaal', 'jaar'].map(p => (
                      <button
                        key={p}
                        type="button"
                        className={`btn btn-sm ${periode === p ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handlePeriodeChange(p)}
                      >
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="col-md-5">
                  <label className="form-label fw-bold text-muted">Specifiek Item</label>
                  <select
                    className="form-select form-select-sm"
                    value={specificFilter}
                    onChange={(e) => setSpecificFilter(e.target.value)}
                  >
                    <option value="">Alle boekingen</option>
                    {Array.isArray(availableItems.ervaringen) && availableItems.ervaringen.length > 0 && (
                      <optgroup label="Ervaringen">
                        {availableItems.ervaringen.map(item => (
                          <option key={`ervaring-${item._id}`} value={`ervaring-${item._id}`}>
                            🌟 {item.naam}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {Array.isArray(availableItems.arrangementen) && availableItems.arrangementen.length > 0 && (
                      <optgroup label="Arrangementen">
                        {availableItems.arrangementen.map(item => (
                          <option key={`arrangement-${item._id}`} value={`arrangement-${item._id}`}>
                            📅 {item.naam}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold text-muted">Sorteren op</label>
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${sortBy === 'omzet' ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => setSortBy('omzet')}
                      title="Sorteer op omzet"
                    >
                      <i className="fas fa-euro-sign me-1"></i>
                      Omzet
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${sortBy === 'aantal' ? 'btn-info' : 'btn-outline-info'}`}
                      onClick={() => setSortBy('aantal')}
                      title="Sorteer op aantal boekingen"
                    >
                      <i className="fas fa-hashtag me-1"></i>
                      Aantal
                    </button>
                  </div>
                </div>
              </div>

              {/* Derde rij: Datum range en acties */}
              <div className="row">
                <div className="col-md-3">
                  <label className="form-label fw-bold text-muted">Van datum</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={tempDatumRange.startDate}
                    onChange={(e) => setTempDatumRange(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold text-muted">Tot datum</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={tempDatumRange.endDate}
                    onChange={(e) => setTempDatumRange(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <button
                    className="btn btn-primary btn-sm w-100"
                    onClick={applyDateRange}
                    disabled={loading}
                    title="Pas datumfilter toe"
                  >
                    <i className="fas fa-check me-1"></i>
                    Toepassen
                  </button>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <button
                    className="btn btn-outline-secondary btn-sm w-100"
                    onClick={() => loadStatistieken()}
                    disabled={loading}
                    title="Herlaad statistieken"
                  >
                    <i className="fas fa-sync-alt me-1"></i>
                    Vernieuwen
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* PDF Content */}
          <div ref={pdfRef}>
            {/* Summary Cards */}
            {statistieken && (
            <>
              <div className="row mb-4">
                <div className="col-md-3 mb-3">
                  <div className="card bg-primary text-white h-100 shadow-sm">
                    <div className="card-body text-center d-flex flex-column justify-content-center">
                      <i className="fas fa-euro-sign fa-3x mb-3 opacity-75"></i>
                      <h6 className="card-subtitle mb-2">Totale Omzet</h6>
                      <h4 className="card-title mb-0">{statistieken.totaleOmzet.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' })}</h4>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="card bg-success text-white h-100 shadow-sm">
                    <div className="card-body text-center d-flex flex-column justify-content-center">
                      <i className="fas fa-calendar-check fa-3x mb-3 opacity-75"></i>
                      <h6 className="card-subtitle mb-2">Aantal Boekingen</h6>
                      <h4 className="card-title mb-0">{statistieken.aantalBoekingen}</h4>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="card bg-info text-white h-100 shadow-sm">
                    <div className="card-body text-center d-flex flex-column justify-content-center">
                      <i className="fas fa-chart-line fa-3x mb-3 opacity-75"></i>
                      <h6 className="card-subtitle mb-2">Gemiddelde Waarde</h6>
                      <h4 className="card-title mb-0">{(statistieken.totaleOmzet / statistieken.aantalBoekingen || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' })}</h4>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="card bg-warning text-white h-100 shadow-sm">
                    <div className="card-body text-center d-flex flex-column justify-content-center">
                      <i className="fas fa-users fa-3x mb-3 opacity-75"></i>
                      <h6 className="card-subtitle mb-2">Totaal Personen</h6>
                      <h4 className="card-title mb-0">{statistieken.totaalPersonen}</h4>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row mb-5">
                <div className="col-md-6 offset-md-3">
                  <div className="card text-white shadow" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <div className="card-body text-center py-4">
                      <i className="fas fa-clock fa-3x mb-3 opacity-75"></i>
                      <h5 className="card-subtitle mb-2">Gemiddeld van Tevoren Geboekt</h5>
                      <h2 className="card-title mb-0 fw-bold">{Math.round(statistieken.gemiddeldeDagenVooruitGeboekt || 0)} dagen</h2>
                      <small className="opacity-75">Tijd tussen boeking en activiteit</small>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Charts */}
          <div className="row mb-4">
            <div className="col-md-8">
              <div className="card">
                <div className="card-header">
                  <h5>
                    <i className="fas fa-chart-area me-2 text-primary"></i>
                    Omzet per {periode}
                  </h5>
                </div>
                <div className="card-body">
                  {getOmzetChartOptions() && (
                    <ApexCharts
                      options={getOmzetChartOptions()}
                      series={getOmzetChartSeries()}
                      type="area"
                      height={350}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card">
                <div className="card-header">
                  <h5>
                    <i className="fas fa-chart-pie me-2 text-success"></i>
                    Boekingen per Type
                  </h5>
                </div>
                <div className="card-body">
                  {getBoekingTypesOptions() && (
                    <ApexCharts
                      options={getBoekingTypesOptions()}
                      series={getBoekingTypesSeries()}
                      type="donut"
                      height={350}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-12">
              <div className="card">
                <div className="card-header">
                  <h5>
                    <i className="fas fa-chart-bar me-2 text-info"></i>
                    Aantal Boekingen per {periode}
                  </h5>
                </div>
                <div className="card-body">
                  {getAantalBoekingenChartOptions() && (
                    <ApexCharts
                      options={getAantalBoekingenChartOptions()}
                      series={getAantalBoekingenSeries()}
                      type="bar"
                      height={350}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Booking Advance Time Analysis */}
          <div className="row mb-4">
            <div className="col-md-8">
              <div className="card">
                <div className="card-header">
                  <h5>
                    <i className="fas fa-clock me-2 text-purple"></i>
                    Gemiddelde Dagen van Tevoren Geboekt per {periode}
                  </h5>
                </div>
                <div className="card-body">
                  {getVooruitBoekenChartOptions() && (
                    <ApexCharts
                      options={getVooruitBoekenChartOptions()}
                      series={getVooruitBoekenSeries()}
                      type="line"
                      height={350}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card">
                <div className="card-header">
                  <h5>
                    <i className="fas fa-chart-pie me-2 text-purple"></i>
                    Verdeling Booking Tijdspanne
                  </h5>
                </div>
                <div className="card-body">
                  {getBoekingTijdspanneOptions() && (
                    <ApexCharts
                      options={getBoekingTijdspanneOptions()}
                      series={getBoekingTijdspanneSeries()}
                      type="donut"
                      height={350}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Top Performing Charts */}
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5>
                    <i className="fas fa-trophy me-2 text-warning"></i>
                    Top 10 Arrangementen ({sortBy === 'omzet' ? 'Omzet' : 'Aantal Boekingen'})
                  </h5>
                </div>
                <div className="card-body">
                  {getTopArrangementenOptions() && (
                    <ApexCharts
                      options={getTopArrangementenOptions()}
                      series={getTopArrangementenSeries()}
                      type="bar"
                      height={400}
                    />
                  )}
                  {(!statistieken?.topArrangementen || statistieken.topArrangementen.length === 0) && (
                    <div className="text-center py-4 text-muted">
                      <i className="fas fa-info-circle fa-2x mb-2"></i>
                      <p>Geen arrangement data beschikbaar voor deze periode</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5>
                    <i className="fas fa-star me-2 text-success"></i>
                    Top 10 Ervaringen ({sortBy === 'omzet' ? 'Omzet' : 'Aantal Boekingen'})
                  </h5>
                </div>
                <div className="card-body">
                  {getTopErvaringenOptions() && (
                    <ApexCharts
                      options={getTopErvaringenOptions()}
                      series={getTopErvaringenSeries()}
                      type="bar"
                      height={400}
                    />
                  )}
                  {(!statistieken?.topErvaringen || statistieken.topErvaringen.length === 0) && (
                    <div className="text-center py-4 text-muted">
                      <i className="fas fa-info-circle fa-2x mb-2"></i>
                      <p>Geen ervaring data beschikbaar voor deze periode</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Tables */}
          <div className="row mb-4">
            {/* Top Arrangementen Table */}
            {statistieken && statistieken.topArrangementen && statistieken.topArrangementen.length > 0 && (
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">
                    <h5>
                      <i className="fas fa-list me-2 text-warning"></i>
                      Top Arrangementen Details
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-sm table-hover">
                        <thead>
                          <tr>
                            <th>Arrangement</th>
                            <th className="text-end">Boekingen</th>
                            <th className="text-end">Personen</th>
                            <th className="text-end">Omzet</th>
                          </tr>
                        </thead>
                        <tbody>
                          {statistieken.topArrangementen.map((item, index) => (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <span className="badge bg-warning me-2">{index + 1}</span>
                                  <strong>{item.naam}</strong>
                                </div>
                              </td>
                              <td className="text-end">{item.aantal}</td>
                              <td className="text-end">{item.personen}</td>
                              <td className="text-end">
                                {item.omzet.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Top Ervaringen Table */}
            {statistieken && statistieken.topErvaringen && statistieken.topErvaringen.length > 0 && (
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">
                    <h5>
                      <i className="fas fa-list me-2 text-success"></i>
                      Top Ervaringen Details
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-sm table-hover">
                        <thead>
                          <tr>
                            <th>Ervaring</th>
                            <th className="text-end">Boekingen</th>
                            <th className="text-end">Personen</th>
                            <th className="text-end">Omzet</th>
                          </tr>
                        </thead>
                        <tbody>
                          {statistieken.topErvaringen.map((item, index) => (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <span className="badge bg-success me-2">{index + 1}</span>
                                  <strong>{item.naam}</strong>
                                </div>
                              </td>
                              <td className="text-end">{item.aantal}</td>
                              <td className="text-end">{item.personen}</td>
                              <td className="text-end">
                                {item.omzet.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Period Overview Table */}
          {statistieken && statistieken.periodenData && (
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-header">
                    <h5>
                      <i className="fas fa-table me-2 text-secondary"></i>
                      Gedetailleerd Overzicht per {periode}
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-striped table-hover">
                        <thead>
                          <tr>
                            <th>Periode</th>
                            <th className="text-end">Aantal Boekingen</th>
                            <th className="text-end">Totaal Personen</th>
                            <th className="text-end">Omzet</th>
                            <th className="text-end">Gemiddelde per Boeking</th>
                            <th className="text-end">Gem. Dagen van Tevoren</th>
                          </tr>
                        </thead>
                        <tbody>
                          {statistieken.periodenData.map((item, index) => (
                            <tr key={index}>
                              <td><strong>{item.periode}</strong></td>
                              <td className="text-end">{item.aantalBoekingen}</td>
                              <td className="text-end">{item.totaalPersonen}</td>
                              <td className="text-end">
                                {item.omzet.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' })}
                              </td>
                              <td className="text-end">
                                {(item.omzet / item.aantalBoekingen || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' })}
                              </td>
                              <td className="text-end">
                                <span className="badge text-white" style={{ backgroundColor: '#8b5cf6' }}>
                                  {Math.round(item.gemiddeldeDagenVooruitGeboekt || 0)} dagen
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  username: state.auth.user,
});

export default connect(mapStateToProps)(BoekingenStatistieken);